import React from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';

const Process = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const steps = [
    {
      number: 1,
      title: 'Discovery',
      description: 'We start with an initial consultation to understand your needs and establish if we\'re the right fit for each other.'
    },
    {
      number: 2,
      title: 'Goal Setting',
      description: 'Together, we define clear, measurable goals and create a personalised coaching roadmap.'
    },
    {
      number: 3,
      title: 'Coaching Journey',
      description: 'Regular sessions where we explore challenges, develop strategies, and build new skills and perspectives.'
    },
    {
      number: 4,
      title: 'Transformation',
      description: 'Implementation of new approaches leads to lasting change and achievement of your goals.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <ProcessContainer>
      <div className="container">
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5 }}
        >
          The Coaching Process
        </SectionTitle>
        
        <ProcessIntro
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          A structured yet flexible approach designed to create meaningful, lasting change
        </ProcessIntro>
        
        <TimelineContainer 
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {steps.map((step, index) => (
            <TimelineStep 
              key={step.number} 
              variants={itemVariants}
              custom={index}
            >
              <TimelineNumberWrapper>
                <TimelineNumber>
                  <span>{step.number}</span>
                  <TimelinePulse />
                </TimelineNumber>
              </TimelineNumberWrapper>
              
              <TimelineContent>
                <TimelineTitle>{step.title}</TimelineTitle>
                <TimelineDescription>{step.description}</TimelineDescription>
              </TimelineContent>
              
              <StepDecoration className="decoration" />
            </TimelineStep>
          ))}
          
          <TimelineLine inView={inView} />
        </TimelineContainer>
        
        <ProcessCta
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 1.2 }}
        >
          <CtaTextContainer>
            <CtaAccent />
            <CtaText>Ready to begin your <Highlight>coaching journey</Highlight>?</CtaText>
          </CtaTextContainer>
          <CtaButton onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })}>
            Book Your Coaching Session
          </CtaButton>
        </ProcessCta>
      </div>
      <BackgroundElement className="element-1" />
      <BackgroundElement className="element-2" />
      <DiagonalDivider />
    </ProcessContainer>
  );
};

const ProcessContainer = styled.section`
  background-color: white;
  position: relative;
  overflow: hidden;
  padding: 100px 0 120px;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 25px;
  position: relative;
  color: #1A2A3A;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: #A8C4A0;
  }
`;

const ProcessIntro = styled(motion.p)`
  text-align: center;
  max-width: 600px;
  margin: 0 auto 70px;
  color: #5A7F8C;
  font-size: 1.2rem;
  line-height: 1.6;
  font-weight: 300;
`;

const TimelineContainer = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  position: relative;
  max-width: 1100px;
  margin: 0 auto 60px;
  z-index: 2;
  
  @media (max-width: 992px) {
    flex-direction: column;
    max-width: 500px;
  }
`;

const TimelineLine = styled.div`
  position: absolute;
  top: 40px;
  left: 50px;
  right: 50px;
  height: 3px;
  background-color: #e0e0e0;
  z-index: 0;
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: ${props => props.inView ? '100%' : '0'};
    height: 100%;
    background-color: #A8C4A0; /* Changed from gradient to solid color */
    transition: width 1.5s ease;
    z-index: 1;
  }
  
  @media (max-width: 992px) {
    top: 0;
    bottom: 0;
    left: 40px;
    width: 3px;
    height: auto;
    right: auto;
    
    &:after {
      width: 3px;
      height: ${props => props.inView ? '100%' : '0'};
      background-color: #A8C4A0; /* Changed from gradient to solid color */
    }
  }
`;

const TimelineStep = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 23%;
  position: relative;
  z-index: 2;
  
  &:hover {
    .decoration {
      transform: translateY(-5px) scale(1.05);
    }
  }
  
  @media (max-width: 992px) {
    flex-direction: row;
    width: 100%;
    text-align: left;
    margin-bottom: 40px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const TimelineNumberWrapper = styled.div`
  margin-bottom: 25px;
  position: relative;
  z-index: 3;
  
  @media (max-width: 992px) {
    margin-bottom: 0;
    margin-right: 30px;
  }
`;

const TimelineNumber = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #5A7F8C; /* Changed from gradient to solid color */
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  font-weight: 600;
  box-shadow: 0 10px 25px rgba(90, 127, 140, 0.3);
  position: relative;
  
  span {
    position: relative;
    z-index: 1;
  }
  
  @media (max-width: 576px) {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
  }
`;

const TimelinePulse = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  animation: pulse 3s infinite;
  
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 0.5;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.1;
    }
    100% {
      transform: scale(1);
      opacity: 0.5;
    }
  }
`;

const TimelineContent = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: 100%;
  position: relative;
  
  &:hover {
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
    transform: translateY(-5px);
  }
  
  @media (max-width: 992px) {
    flex: 1;
  }
`;

const TimelineTitle = styled.h3`
  font-size: 1.4rem;
  color: #1A2A3A;
  margin-bottom: 15px;
  position: relative;
  padding-bottom: 12px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 2px;
    background-color: #A8C4A0;
  }
  
  @media (max-width: 992px) {
    &:after {
      left: 0;
      transform: none;
    }
  }
`;

const TimelineDescription = styled.p`
  color: #555;
  line-height: 1.6;
  font-size: 1rem;
`;

const StepDecoration = styled.div`
  position: absolute;
  width: 160px;
  height: 160px;
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  background-color: rgba(168, 196, 160, 0.03);
  bottom: -30px;
  right: -30px;
  z-index: -1;
  transition: all 0.5s ease;
  
  @media (max-width: 992px) {
    width: 120px;
    height: 120px;
  }
`;

const ProcessCta = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: white;
  padding: 40px;
  border-radius: 12px;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(168, 196, 160, 0.2);
`;

const CtaTextContainer = styled.div`
  position: relative;
  margin-bottom: 30px;
`;

const CtaAccent = styled.div`
  position: absolute;
  top: -25px;
  left: -15px;
  width: 50px;
  height: 50px;
  background-color: rgba(90, 127, 140, 0.1);
  border-radius: 50%;
  z-index: -1;
`;

const CtaText = styled.h3`
  font-size: 1.8rem;
  color: #1A2A3A;
  position: relative;
  font-weight: 600;
`;

const Highlight = styled.span`
  position: relative;
  color: #5A7F8C;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 2px;
    left: 0;
    width: 100%;
    height: 5px;
    background-color: rgba(168, 196, 160, 0.3);
    z-index: -1;
  }
`;

const CtaButton = styled.button`
  display: inline-block;
  padding: 15px 30px;
  background-color: #A8C4A0; /* Changed from gradient to solid color */
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.05rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(168, 196, 160, 0.3);
  
  &:hover {
    background-color: #97b38f;
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(168, 196, 160, 0.4);
  }
`;

const BackgroundElement = styled.div`
  position: absolute;
  
  &.element-1 {
    width: 600px;
    height: 600px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v50H50v50H0z' fill='%23A8C4A0' fill-opacity='0.05'/%3E%3C/svg%3E");
    top: -200px;
    right: -200px;
    z-index: 0;
    transform: rotate(-15deg);
  }
  
  &.element-2 {
    width: 500px;
    height: 500px;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h50v50H0z' fill='%235A7F8C' fill-opacity='0.03'/%3E%3C/svg%3E");
    bottom: -100px;
    left: -150px;
    z-index: 0;
    transform: rotate(10deg);
  }
`;

const DiagonalDivider = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background-color: #f9f9f9;
  clip-path: polygon(0 100%, 100% 40%, 100% 100%, 0 100%);
  z-index: 1;
`;

export default Process;